// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import sumBy from 'lodash/sumBy';

import {SectionItem} from 'common/components/items-menu/types';
import {formatMetricNumber, formatPercent} from 'common/util';
import {colors, getChartSegmentColorCombo} from 'common/util/colors';
import {FlatSortConfig, SortBy, SortConfig, SortOrder} from 'common/types/reports/bookmark';

import {Bar, Header, Segment, SegmentWithChildren} from './types';
import {BarPattern, BarValueType} from './types';

export const VALUE_LABEL = `Value`;
export const MINIMUM_COLUMN_WIDTH = 64;
export const DEFAULT_COLUMN_WIDTH = 160;

export const DEFAULT_VALUE_PERCENTAGE_WIDTH = 40;

// 50px and 4px padding on each side
// manually determined to account for 4 sigfigs and a unit e.g. 287.4K
export const BAR_TEXT_LABEL_WIDTH = 58;
export const MIN_BAR_SEGMENT_WIDTH = 2;

export function segmentHasChildren(n: Segment): n is SegmentWithChildren {
  return Object.prototype.hasOwnProperty.call(n, `children`) && !Object.prototype.hasOwnProperty.call(n, `bars`);
}

export function segmentDepth(segment: Segment): number {
  let depth = 0;
  let curr: Segment = segment;

  while (curr) {
    if (segmentHasChildren(curr)) {
      curr = curr.children[0];
      depth += 1;
    } else {
      return depth;
    }
  }

  return depth;
}

const ALPHA_SORT_OPTIONS: Array<SectionItem<FlatSortConfig>> = [
  {
    label: `Alpha A → Z`,
    icon: `sort-alpha-asc`,
    value: {
      sortBy: SortBy.Label,
      sortOrder: SortOrder.Ascending,
    },
  },
  {
    label: `Alpha Z → A`,
    icon: `sort-alpha-desc`,
    value: {
      sortBy: SortBy.Label,
      sortOrder: SortOrder.Descending,
    },
  },
];

export const VALUE_SORT_OPTIONS: Array<SectionItem<FlatSortConfig>> = [
  {
    label: `Value 1 → 100`,
    icon: `sort-value-asc`,
    value: {
      sortBy: SortBy.Value,
      sortOrder: SortOrder.Ascending,
    },
  },
  {
    label: `Value 100 → 1`,
    icon: `sort-value-desc`,
    value: {
      sortBy: SortBy.Value,
      sortOrder: SortOrder.Descending,
    },
  },
];

export const COLUMN_SORT_OPTIONS = [...ALPHA_SORT_OPTIONS, ...VALUE_SORT_OPTIONS];

function setSelectedSortOption(
  options: Array<SectionItem<FlatSortConfig>>,
  selectedOption: FlatSortConfig,
): Array<SectionItem<FlatSortConfig>> {
  return options.map((option) => {
    if (isEqual(option.value, selectedOption)) {
      return {...option, isSelected: true};
    }
    return option;
  });
}

/** Generate header labels and sort options for each if sortConfig is provided */
export function generateHeaders(segment: Segment, sortConfig?: SortConfig): Header[] {
  const headers: Header[] = [];
  let curr: Segment = segment;
  let depth = 0;

  const colSortAttrs = sortConfig?.sortBy === SortBy.Column ? sortConfig.colSortAttrs : null;
  const valueSortOption =
    sortConfig?.sortBy === SortBy.Value ? {sortBy: SortBy.Value, sortOrder: sortConfig.sortOrder} : null;

  while (curr) {
    headers.push({
      label: curr.segmentName,
      sortOptionItems: colSortAttrs
        ? setSelectedSortOption(COLUMN_SORT_OPTIONS, colSortAttrs[depth])
        : COLUMN_SORT_OPTIONS,
    });

    if (segmentHasChildren(curr)) {
      curr = curr.children[0];
    } else {
      curr = null;
    }

    depth++;
  }

  headers.push({
    label: VALUE_LABEL,
    sortOptionItems: valueSortOption
      ? setSelectedSortOption(VALUE_SORT_OPTIONS, valueSortOption as FlatSortConfig)
      : VALUE_SORT_OPTIONS,
  });

  return headers;
}

export function minimumSegmentValue(segment: Segment): number {
  let queue: Segment[] = [segment];

  let currMin = Infinity;

  while (queue.length) {
    const curr: Segment = queue.pop();

    if (segmentHasChildren(curr)) {
      queue = queue.concat(curr.children);
    } else {
      curr.bars.forEach((bar: Bar) => {
        const segments = bar.negativeSegments.length ? bar.negativeSegments : bar.segments;
        const sumSegments = sumBy(segments, `value`);

        if (sumSegments < currMin) {
          currMin = sumSegments;
        }
      });
    }
  }

  return currMin;
}

export function maximumSegmentValue(segment: Segment): number {
  let queue: Segment[] = [segment];

  let currMax = -Infinity;

  while (queue.length) {
    const curr: Segment = queue.pop();

    if (segmentHasChildren(curr)) {
      queue = queue.concat(curr.children);
    } else {
      curr.bars.forEach((bar: Bar) => {
        const segments = bar.segments.length ? bar.segments : bar.negativeSegments;
        const sumSegments = sumBy(segments, `value`);

        if (sumSegments > currMax) {
          currMax = sumSegments;
        }
      });
    }
  }

  return currMax;
}

export function assignColorsToSegmentArray(segments: Segment[]): Segment[] {
  let colorIdx = 0;

  return segments.map((seg) => {
    const [segment, newColorOffset] = assignColorToSegment(seg, colorIdx);
    colorIdx = newColorOffset;

    return segment;
  });
}

export function assignColorToSegment(segment: Segment, colorIdx: number): [Segment, number] {
  if (segmentHasChildren(segment)) {
    return [
      {
        ...segment,
        children: segment.children.map((seg) => {
          const [updatedSegment, newColorIdx] = assignColorToSegment(seg, colorIdx);
          colorIdx = newColorIdx;

          return updatedSegment;
        }),
      },
      colorIdx,
    ];
  }

  const bars = assignColorToBars(segment.bars, colorIdx);
  colorIdx++; // increment colorIdx since we've used this color

  return [
    {
      ...segment,
      bars,
    },
    colorIdx,
  ];
}

export function assignColorToBars(bars: Bar[], barColor: number): Bar[] {
  return bars.map((bar) => ({
    ...bar,
    segments: bar.segments.map((segment, idx) => {
      const defaultCombo = getChartSegmentColorCombo(barColor + idx);

      return {
        ...segment,
        color: segment.color || defaultCombo.primary,
      };
    }),
  }));
}

export function getBarBackground(
  barPattern: BarPattern,
  barColor: string,
): {backgroundColor: string; backgroundImage?: string; backgroundSize?: string} {
  return {
    backgroundColor: barColor,
    ...(barPattern === BarPattern.Striped
      ? {
          backgroundImage: `repeating-linear-gradient(-45deg, ${barColor}, ${barColor} 25%, ${colors.gray0} 25%, ${colors.gray0} 50%, ${barColor} 50%)`,
          backgroundSize: `4px 4px`,
        }
      : {}),
  };
}

export function barChartLog(value) {
  return Math.abs(value) > 1 ? Math.log10(Math.abs(value)) : 0;
}

export function getBarChartDisplayValues({
  value,
  percent,
  segmentMax,
  barValueType,
}: {
  value: number;
  percent: number;
  segmentMax: number;
  barValueType: BarValueType;
}) {
  const isLogarithmic = barValueType === BarValueType.Log;
  const isAbsolute = barValueType === BarValueType.Absolute;

  let maxWidth = segmentMax;
  let width;

  if (isLogarithmic) {
    width = barChartLog(value);
    maxWidth = barChartLog(segmentMax);
  } else {
    if (isAbsolute) {
      width = value;
    } else {
      width = percent;
    }
  }

  const widthAsPercentage = width / maxWidth || 0;

  return {
    abbreviatedValue: formatMetricNumber(value),
    percentValueString: formatPercent(percent),
    widthAsPercentage,
  };
}

/**
 *  Truncate segments to a maximum of @num leaves (aka SegmentWithoutChildren)
 */
export function truncateSegments(data: Segment[], num: number): {segments: Segment[]; isTruncated: boolean} {
  let numSegmentsToShow = num;

  function helper(segments: Segment[]): Segment[] {
    if (numSegmentsToShow <= 0 || !segments.length) {
      return [];
    }

    if (segmentHasChildren(segments[0])) {
      return segments
        .map((segment: SegmentWithChildren) => {
          const children = helper(segment.children);
          return {...segment, children};
        })
        .filter((segment: SegmentWithChildren) => !!segment.children.length);
    }
    const truncated = segments.slice(0, numSegmentsToShow);
    numSegmentsToShow -= truncated.length;
    return truncated;
  }

  return {segments: helper(data), isTruncated: numSegmentsToShow <= 0};
}

/**
 * Based on the width of the component and number of columns, determine the ideal percentage width for each column and the value column.
 */
export function getResponsiveColumnWidths(
  width: number,
  numColumns: number,
): {labelWidthPercentage: number; valueWidthPercentage: number} {
  // special case for small widths, need to make value width higher to ensure readability
  if (width < 512) {
    return {
      // 1 column for segment and 1 for value in single view
      labelWidthPercentage: 40,
      valueWidthPercentage: 60,
    };
  }

  const percentageForColumns = 100 - DEFAULT_VALUE_PERCENTAGE_WIDTH;

  const goalColumnPercentage = (DEFAULT_COLUMN_WIDTH / width) * 100;

  let columnPercentage;
  if (numColumns * goalColumnPercentage < percentageForColumns) {
    // if all columns can fit into view with the goal percentage, just use that
    columnPercentage = goalColumnPercentage;
  } else {
    // get as close to the goal percentage as possible while making sure "numColumnsToFitInView * columnPercentage = percentageForColumns" holds
    // this ensures we can snugly fit a certain number of columns in the scroll window.
    columnPercentage = percentageForColumns;
    let numColumnsToFitInView = 1;
    while (columnPercentage > goalColumnPercentage) {
      columnPercentage = percentageForColumns / ++numColumnsToFitInView;
    }
  }

  return {
    labelWidthPercentage: columnPercentage,
    valueWidthPercentage: DEFAULT_VALUE_PERCENTAGE_WIDTH,
  };
}
